import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/Graphs/Dialogue";
import { Button } from "components/Graphs/Button";
import { RadioGroup, RadioGroupItem } from "components/Graphs/RadioGroup";
import InputField from "components/UI-lib/InputField";
import type { OrgAPIKey, KeyRole } from "./useOrgAPIKeys";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  apiKey: OrgAPIKey | null;
  availableRoles: KeyRole[];
  onSubmit: (req: {
    id: string;
    name: string;
    roleIds: string[];
    expiresAt: string;
  }) => Promise<void>;
};

function isExpired(ts?: string): boolean {
  if (!ts) return false;
  const date = new Date(ts);
  if (isNaN(date.getTime())) return false;
  return date.getTime() <= Date.now();
}

const EditAPIKeyModal: React.FC<Props> = ({
  open,
  onOpenChange,
  apiKey,
  availableRoles,
  onSubmit,
}) => {
  const [name, setName] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const apiKeyExpired = isExpired(apiKey?.expiresAt);

  useEffect(() => {
    if (open && apiKey) {
      setName(apiKey.name);
      setSelectedRoleId(apiKey.roleIds?.[0] ?? "");
      setError("");
    }
  }, [open, apiKey]);

  const handleSubmit = async () => {
    if (!apiKey) return;
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    if (!selectedRoleId) {
      setError("Select an access level");
      return;
    }
    setIsSubmitting(true);
    setError("");
    try {
      await onSubmit({
        id: apiKey.id,
        name: name.trim(),
        roleIds: [selectedRoleId],
        expiresAt: apiKey.expiresAt,
      });
      onOpenChange(false);
    } catch (e: any) {
      setError(e?.message || "Failed to update API key");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit API Key</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <InputField
            label="Name"
            value={name}
            onChange={setName}
            placeholder="e.g., CI/CD pipeline"
            errorMessage=""
            disabled={apiKeyExpired}
          />

          {apiKeyExpired && (
            <div className="rounded-md border border-red-900 bg-red-950/40 p-3 text-sm text-red-300">
              Expired API keys can only be deleted.
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
              Access
            </label>
            <RadioGroup
              value={selectedRoleId}
              onValueChange={setSelectedRoleId}
              disabled={apiKeyExpired}
              className="space-y-3 max-h-48 overflow-y-auto border border-zinc-800 rounded-md p-3"
            >
              {availableRoles.map((role) => {
                const id = `edit-api-key-role-${role.id}`;
                return (
                  <div key={role.id} className="flex items-start gap-2">
                    <RadioGroupItem
                      value={role.id}
                      id={id}
                      className="mt-0.5"
                    />
                    <label htmlFor={id} className="cursor-pointer">
                      <span className="block text-sm text-gray-900 dark:text-white">
                        {role.displayName}
                      </span>
                      <span className="block text-xs text-secondary">
                        {role.description}
                      </span>
                    </label>
                  </div>
                );
              })}
              {availableRoles.length === 0 && (
                <div className="text-sm text-secondary">
                  No access levels available.
                </div>
              )}
            </RadioGroup>
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            isLoading={isSubmitting}
            disabled={apiKeyExpired}
            onClick={handleSubmit}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditAPIKeyModal;
